/**
 * clientInfo.ts
 *
 * Describes the current browser for support staff. Prefers User-Agent Client
 * Hints, which report the real OS version and model, and falls back to the UA
 * string, which modern Chrome freezes ("Android 10; K", "Mac OS X 10_15_7").
 */
import UAParser from "ua-parser-js";
import { getOrCreateDeviceId } from "@/utils/deviceId";

/** How long login waits for Client Hints before sending UA-only data. */
export const HINTS_TIMEOUT_MS = 300;

/** The fields of the `ClientInfo` proto message. */
export interface ClientInfoFields {
  deviceId: string;
  browserName: string;
  browserVersion: string;
  osName: string;
  osVersion: string;
  deviceType: string;
  deviceModel: string;
  screen: string;
  userAgent: string;
}

/** The subset of `UADataValues` this module reads. */
export interface HighEntropyHints {
  platform?: string;
  platformVersion?: string;
  model?: string;
  fullVersionList?: { brand: string; version: string }[];
}

/** Screen facts that are not in the UA. */
export interface ScreenInfo {
  width: number;
  height: number;
  pixelRatio: number;
  maxTouchPoints: number;
}

/**
 * Builds a `ClientInfo` from a UA string and optional Client Hints. Pure.
 *
 * @param input.userAgent - `navigator.userAgent`.
 * @param input.deviceId - This browser's id (see `getOrCreateDeviceId`).
 * @param input.hints - High-entropy Client Hints, when the browser has them.
 * @param input.screen - Screen size and touch support.
 * @returns Every field filled; unknown values are empty strings.
 */
export function parseClientInfo(input: {
  userAgent: string;
  deviceId: string;
  hints?: HighEntropyHints;
  screen?: ScreenInfo;
}): ClientInfoFields {
  const { userAgent, deviceId, hints, screen } = input;
  const ua = new UAParser(userAgent).getResult();

  const info: ClientInfoFields = {
    deviceId,
    browserName: ua.browser.name ?? "",
    browserVersion: ua.browser.version ?? "",
    osName: ua.os.name ?? "",
    osVersion: ua.os.version ?? "",
    deviceType: ua.device.type ?? "desktop",
    // Reduced UAs carry the placeholder model "K"; that is not a real model.
    deviceModel: ua.device.model === "K" ? "" : (ua.device.model ?? ""),
    screen: screen ? `${screen.width}x${screen.height}@${screen.pixelRatio}` : "",
    userAgent,
  };

  if (hints) applyHints(info, hints);
  if (info.osName === "Mac OS" && (screen?.maxTouchPoints ?? 0) > 1) {
    // iPadOS 13+ sends a desktop Mac UA. Macs have no touch screen.
    info.osName = "iPadOS";
    info.osVersion = "";
    info.deviceType = "tablet";
    info.deviceModel = "iPad";
  }
  return info;
}

/**
 * Collects this browser's `ClientInfo`. Never throws and never waits longer
 * than `HINTS_TIMEOUT_MS` for Client Hints.
 *
 * @returns The info, or `undefined` if even the UA could not be read.
 */
export async function collectClientInfo(): Promise<ClientInfoFields | undefined> {
  try {
    const hints = await readHints();
    return parseClientInfo({
      userAgent: navigator.userAgent,
      deviceId: getOrCreateDeviceId(),
      hints,
      screen: readScreen(),
    });
  } catch {
    return undefined;
  }
}

// =============================================================================
// Helper Functions
// =============================================================================

interface NavigatorUAData {
  getHighEntropyValues(keys: string[]): Promise<HighEntropyHints>;
}

/** Overwrites the UA-derived values that Client Hints report more accurately. */
function applyHints(info: ClientInfoFields, hints: HighEntropyHints): void {
  if (hints.platformVersion) {
    if (hints.platform !== "Windows") {
      info.osVersion = hints.platformVersion;
    } else {
      info.osVersion = windowsRelease(hints.platformVersion) ?? info.osVersion;
    }
  }
  if (hints.model) info.deviceModel = hints.model;

  const name = info.browserName.toLowerCase();
  const match = name
    ? hints.fullVersionList?.find((b) => b.brand.toLowerCase().includes(name))
    : undefined;
  if (match) info.browserVersion = match.version;
}

/**
 * Windows 11 reports platformVersion 13+; 1–12 are Windows 10. Windows 7/8/8.1
 * report "0.x", which says nothing useful, so those return undefined and the
 * UA-derived version stays.
 */
function windowsRelease(platformVersion: string): string | undefined {
  const major = Number.parseInt(platformVersion, 10);
  if (!(major >= 1)) return undefined;
  return major >= 13 ? "11" : "10";
}

async function readHints(): Promise<HighEntropyHints | undefined> {
  const uaData = (navigator as Navigator & { userAgentData?: NavigatorUAData }).userAgentData;
  if (!uaData?.getHighEntropyValues) return undefined;

  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<undefined>((resolve) => {
    timer = setTimeout(() => resolve(undefined), HINTS_TIMEOUT_MS);
  });
  try {
    return await Promise.race([
      uaData
        .getHighEntropyValues(["platformVersion", "model", "fullVersionList"])
        .catch(() => undefined),
      timeout,
    ]);
  } finally {
    clearTimeout(timer);
  }
}

function readScreen(): ScreenInfo | undefined {
  if (typeof window === "undefined" || !window.screen) return undefined;
  return {
    width: window.screen.width,
    height: window.screen.height,
    pixelRatio: window.devicePixelRatio,
    maxTouchPoints: navigator.maxTouchPoints ?? 0,
  };
}
